import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Upload } from 'lucide-react';
import { supabase } from '../supabaseClient';

export default function ProfileClient({ profileData, onSave, onNavigate }) {
    const [formData, setFormData] = useState(profileData || {});
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [photoPreview, setPhotoPreview] = useState(profileData?.photo_path || null);

    React.useEffect(() => {
        if (profileData) {
            setFormData(profileData);
            setPhotoPreview(profileData.photo_path || null);
        }
    }, [profileData]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type and size
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('Image must be under 5MB.');
            return;
        }

        setUploading(true);
        try {
            // Use a folder prefix — required by some bucket policies
            const ext = file.name.split('.').pop();
            const filename = `profiles/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('user-photos')
                .upload(filename, file, {
                    contentType: file.type,
                    upsert: true
                });

            if (uploadError) {
                console.error('Upload error details:', uploadError);
                throw new Error(uploadError.message || 'Upload failed');
            }

            const { data: { publicUrl } } = supabase.storage
                .from('user-photos')
                .getPublicUrl(filename);

            setPhotoPreview(publicUrl);
            setFormData(prev => ({ ...prev, photo_path: publicUrl }));
        } catch (err) {
            console.error('Photo upload error:', err);
            alert(`Photo upload failed: ${err.message}\n\nMake sure the user-photos bucket exists in Supabase Storage and has an upload policy enabled.`);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const result = await onSave(formData);
        setSaving(false);
        if (result?.success) {
            setIsEditing(false);
        } else if (result?.error) {
            alert(`Save failed: ${result.error}`);
        }
    };

    return (
        <div className="profile-page">
            <div className="profile-header">
                <button className="back-button" onClick={() => onNavigate('dashboard')}>
                    ← Back to Dashboard
                </button>
                <h1>Client Profile</h1>
                {!isEditing && (
                    <button className="edit-button" onClick={() => setIsEditing(true)}>
                        Edit Profile
                    </button>
                )}
            </div>

            {!isEditing ? (
                /* VIEW MODE */
                <div className="profile-view-mode">
                    <div className="profile-view-header">
                        <div className="profile-view-photo">
                            {photoPreview ? (
                                <img src={photoPreview} alt="Profile" />
                            ) : (
                                <div className="profile-view-photo-placeholder">
                                    <User size={40} />
                                </div>
                            )}
                        </div>
                        <div className="profile-view-info">
                            <h2>{formData.name || 'No Name'}</h2>
                            <p>{formData.email}</p>
                        </div>
                    </div>

                    <div className="profile-view-grid">
                        <div className="profile-view-section">
                            <h3><Phone size={18} /> Contact Information</h3>
                            <div className="profile-view-field">
                                <div className="profile-view-label">Phone</div>
                                <div className="profile-view-value">{formData.phone || 'Not provided'}</div>
                            </div>
                            <div className="profile-view-field">
                                <div className="profile-view-label">Address</div>
                                <div className="profile-view-value">
                                    {formData.address
                                        ? [formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(', ')
                                        : 'Not provided'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {formData.bio && (
                        <div className="profile-view-bio">
                            <h3>About</h3>
                            <p>{formData.bio}</p>
                        </div>
                    )}
                </div>
            ) : (
                /* EDIT MODE */
                <form onSubmit={handleSubmit} className="profile-form">
                    {/* Photo Upload */}
                    <div className="profile-section photo-section">
                        <div className="photo-upload-container">
                            <div className="photo-preview">
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Profile" />
                                ) : (
                                    <div className="photo-placeholder">
                                        <User size={48} />
                                    </div>
                                )}
                            </div>
                            <label className="photo-upload-button" style={{ opacity: uploading ? 0.6 : 1 }}>
                                <Upload size={16} />
                                {uploading ? 'Uploading...' : 'Upload Photo'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoUpload}
                                    disabled={uploading}
                                    hidden
                                />
                            </label>
                        </div>
                    </div>

                    {/* General Information */}
                    <div className="profile-section">
                        <h2><User size={20} /> General Information</h2>
                        <div className="form-grid">
                            <div className="form-field">
                                <label><User size={16} /> Full Name *</label>
                                <input
                                    type="text"
                                    value={formData.name || ''}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label><Mail size={16} /> Email</label>
                                <input type="email" value={formData.email || ''} disabled />
                            </div>
                            <div className="form-field">
                                <label><Phone size={16} /> Phone *</label>
                                <input
                                    type="tel"
                                    value={formData.phone || ''}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="profile-section">
                        <h2><MapPin size={20} /> Location</h2>
                        <div className="form-grid">
                            <div className="form-field full-width">
                                <label>Address</label>
                                <input
                                    type="text"
                                    value={formData.address || ''}
                                    onChange={(e) => handleChange('address', e.target.value)}
                                    placeholder="Street address"
                                />
                            </div>
                            <div className="form-field">
                                <label>City</label>
                                <input
                                    type="text"
                                    value={formData.city || ''}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                    placeholder="City"
                                />
                            </div>
                            <div className="form-field">
                                <label>State / Province</label>
                                <input
                                    type="text"
                                    value={formData.state || ''}
                                    onChange={(e) => handleChange('state', e.target.value)}
                                    placeholder="State"
                                />
                            </div>
                            <div className="form-field">
                                <label>Zip / Postal Code</label>
                                <input
                                    type="text"
                                    value={formData.zip || ''}
                                    onChange={(e) => handleChange('zip', e.target.value)}
                                    placeholder="Zip code"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bio */}
                    <div className="profile-section">
                        <h2>About You</h2>
                        <div className="form-field full-width">
                            <textarea
                                value={formData.bio || ''}
                                onChange={(e) => handleChange('bio', e.target.value)}
                                placeholder="Tell us about yourself and your legal needs..."
                                maxLength={500}
                                rows={5}
                            />
                            <small>{(formData.bio || '').length}/500 characters</small>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="profile-actions">
                        <button type="submit" className="save-button" disabled={saving || uploading}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                setFormData(profileData || {});
                                setPhotoPreview(profileData?.photo_path || null);
                                setIsEditing(false);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}